import { AlertCircle, Loader2, MapPin, MapPinned, Navigation, Search, X } from 'lucide-react'
import type { Map as MapLibreMap, Marker } from 'maplibre-gl'
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
  type AddressSearchState,
  useMapAddressSearch,
} from '@/features/geolocation/hooks/useMapAddressSearch'
import {
  formatAddressSubtitle,
  type MapAddressResult,
  type ParsedCoordinate,
  parseLatLon,
} from '@/features/geolocation/utils/map-search-utils'
import { pastelFor } from '@/features/geolocation/utils/marker-palette'
import type { GeoEntity } from '@/stores/geolocation-store'
import { useGeolocationStore } from '@/stores/geolocation-store'
import { cn } from '@/utils/cn'
import { addMapNavigationTarget, type MapNavigationTarget } from './map-navigation-target'

interface MapSearchBarProps {
  mapRef: RefObject<MapLibreMap | null>
  className?: string
}

/** Top-left command surface for entities, addresses, and pasted coordinates. */
export function MapSearchBar({ mapRef, className }: MapSearchBarProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const entities = useGeolocationStore(state => state.entities)
  const selectEntity = useGeolocationStore(state => state.selectEntity)

  const coordinate = useMemo(() => parseLatLon(query), [query])
  const entityMatches = useEntityMatches(query, entities)
  const addressSearch = useMapAddressSearch(query, coordinate !== null, open)
  const navigation = useNavigationTarget(mapRef)

  const close = useCallback((nextQuery: string) => {
    setQuery(nextQuery)
    setOpen(false)
    inputRef.current?.blur()
  }, [])
  const dismiss = useCallback(() => {
    navigation.clear()
    close('')
  }, [close, navigation.clear])
  const openSearch = useCallback(() => setOpen(true), [])
  useSearchShortcuts(inputRef, openSearch, dismiss)

  const resultActions = buildResultActions({
    navigation,
    close,
    selectEntity,
    targetAriaLabel: label => t('mapSearch.navigationTarget', { label }),
  })

  return (
    <div
      className={cn(
        // Phone: spans the map up to the tool column on the right. Tablet:
        // narrower, so it clears the tool column that the open sidebar pushes left.
        'pointer-events-auto absolute left-3 right-[4.5rem] top-3 z-10 md:right-auto md:w-[220px] lg:w-[320px]',
        className
      )}
    >
      <SearchField
        inputRef={inputRef}
        model={{ query, placeholder: t('mapSearch.placeholder'), clearLabel: t('mapSearch.clear') }}
        actions={buildSearchFieldActions({
          setQuery,
          setOpen,
          clearTarget: navigation.clear,
        })}
      />
      {open && query.trim().length > 0 && (
        <SearchResults
          model={{ coordinate, entityMatches, addressSearch }}
          actions={resultActions}
        />
      )}
    </div>
  )
}

function useEntityMatches(query: string, entities: GeoEntity[]): GeoEntity[] {
  return useMemo(() => {
    const normalized = query.trim().toLowerCase()
    if (!normalized) return []
    return entities
      .filter(
        entity =>
          entity.name.toLowerCase().includes(normalized) ||
          entity.type.toLowerCase().includes(normalized)
      )
      .slice(0, 8)
  }, [query, entities])
}

function useNavigationTarget(mapRef: RefObject<MapLibreMap | null>) {
  const markerRef = useRef<Marker | null>(null)
  const clear = useCallback(() => {
    markerRef.current?.remove()
    markerRef.current = null
  }, [])
  const show = useCallback(
    (target: MapNavigationTarget, ariaLabel: string, zoom: number) => {
      const map = mapRef.current
      if (!map) return
      clear()
      markerRef.current = addMapNavigationTarget(map, target, ariaLabel)
      map.flyTo({
        center: [target.longitude, target.latitude],
        zoom: Math.max(map.getZoom(), zoom),
        duration: 700,
      })
    },
    [clear, mapRef]
  )
  useEffect(() => clear, [clear])
  return { clear, show }
}

function useSearchShortcuts(
  inputRef: RefObject<HTMLInputElement | null>,
  open: () => void,
  dismiss: () => void
) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault()
        open()
        window.setTimeout(() => inputRef.current?.focus(), 50)
      } else if (event.key === 'Escape') {
        dismiss()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [dismiss, inputRef, open])
}

interface SearchFieldModel {
  query: string
  placeholder: string
  clearLabel: string
}

interface SearchFieldActions {
  onChange: (value: string) => void
  onFocus: () => void
  onBlur: () => void
  onClear: () => void
}

interface SearchFieldActionContext {
  setQuery: (value: string) => void
  setOpen: (open: boolean) => void
  clearTarget: () => void
}

function buildSearchFieldActions(context: SearchFieldActionContext): SearchFieldActions {
  return {
    onChange: context.setQuery,
    onFocus: () => context.setOpen(true),
    onClear: () => {
      context.setQuery('')
      context.clearTarget()
    },
    onBlur: () => window.setTimeout(() => context.setOpen(false), 150),
  }
}

function SearchField({
  inputRef,
  model,
  actions,
}: {
  inputRef: RefObject<HTMLInputElement | null>
  model: SearchFieldModel
  actions: SearchFieldActions
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-full border px-4 py-2',
        'border-border/40 bg-card/72 backdrop-blur-xl backdrop-saturate-150',
        'shadow-[0_18px_48px_-16px_hsl(var(--foreground)/0.28),inset_0_1px_0_hsl(0_0%_100%/0.10)]',
        'text-foreground'
      )}
    >
      <Search className="h-3.5 w-3.5 text-muted-foreground" />
      <input
        ref={inputRef}
        value={model.query}
        onChange={event => actions.onChange(event.target.value)}
        onFocus={actions.onFocus}
        onBlur={actions.onBlur}
        placeholder={model.placeholder}
        className={cn(
          // 16px text on a phone: anything smaller makes iOS zoom the page on focus.
          'min-w-0 flex-1 bg-transparent text-[12px] text-foreground placeholder:text-muted-foreground/70',
          'outline-none focus:outline-none max-md:text-base max-md:placeholder:text-xs'
        )}
      />
      {model.query ? (
        <button
          type="button"
          onClick={actions.onClear}
          className="text-muted-foreground hover:text-foreground max-md:-m-2 max-md:p-2"
          title={model.clearLabel}
          aria-label={model.clearLabel}
        >
          <X className="h-3 w-3" />
        </button>
      ) : (
        <kbd className="rounded border border-border/60 bg-muted/40 px-1.5 py-0.5 font-mono text-[9px] text-muted-foreground max-md:hidden">
          ⌘K
        </kbd>
      )}
    </div>
  )
}

interface SearchResultsModel {
  coordinate: ParsedCoordinate | null
  entityMatches: GeoEntity[]
  addressSearch: AddressSearchState
}

interface SearchResultActions {
  onCoordinate: (point: ParsedCoordinate) => void
  onEntity: (id: string) => void
  onAddress: (result: MapAddressResult) => void
}

interface SearchActionContext {
  navigation: ReturnType<typeof useNavigationTarget>
  close: (nextQuery: string) => void
  selectEntity: (id: string | null) => void
  targetAriaLabel: (label: string) => string
}

function buildResultActions(context: SearchActionContext): SearchResultActions {
  const showTarget = (target: MapNavigationTarget, zoom: number) => {
    context.navigation.show(target, context.targetAriaLabel(target.label), zoom)
    context.close(target.label)
  }
  return {
    onCoordinate: point => {
      const label = `${point.lat.toFixed(5)}, ${point.lng.toFixed(5)}`
      showTarget({ latitude: point.lat, longitude: point.lng, label }, 14)
    },
    onEntity: id => {
      context.navigation.clear()
      context.selectEntity(id)
      context.close('')
    },
    onAddress: result => {
      showTarget({ latitude: result.lat, longitude: result.lon, label: result.display }, 16)
    },
  }
}

function SearchResults({
  model,
  actions,
}: {
  model: SearchResultsModel
  actions: SearchResultActions
}) {
  return (
    <div
      className={cn(
        'mt-2 max-h-[60vh] overflow-auto rounded-2xl border',
        'border-border/40 bg-popover/85 backdrop-blur-xl backdrop-saturate-150 text-popover-foreground',
        'shadow-[0_24px_64px_-16px_hsl(var(--foreground)/0.32),inset_0_1px_0_hsl(0_0%_100%/0.08)]'
      )}
    >
      <CoordinateResults coordinate={model.coordinate} onSelect={actions.onCoordinate} />
      <EntityResults entities={model.entityMatches} onSelect={actions.onEntity} />
      <AddressResults search={model.addressSearch} onSelect={actions.onAddress} />
      <SearchFeedback model={model} />
    </div>
  )
}

function CoordinateResults({
  coordinate,
  onSelect,
}: {
  coordinate: ParsedCoordinate | null
  onSelect: (point: ParsedCoordinate) => void
}) {
  const { t } = useTranslation()
  if (!coordinate) return null
  return (
    <ResultGroup label={t('mapSearch.coordinate')}>
      <ResultRow
        icon={<Navigation className="h-3.5 w-3.5 text-primary" />}
        title={`${coordinate.lat.toFixed(5)}, ${coordinate.lng.toFixed(5)}`}
        subtitle={t('mapSearch.flyToPoint')}
        onClick={() => onSelect(coordinate)}
      />
    </ResultGroup>
  )
}

function EntityResults({
  entities,
  onSelect,
}: {
  entities: GeoEntity[]
  onSelect: (id: string) => void
}) {
  const { t } = useTranslation()
  if (entities.length === 0) return null
  return (
    <ResultGroup label={t('mapSearch.entities')}>
      {entities.map(entity => (
        <ResultRow
          key={entity.id}
          icon={
            <span
              className="h-2.5 w-2.5 rounded-full ring-1 ring-foreground/30"
              style={{ background: pastelFor(entity.label ?? entity.type) }}
            />
          }
          title={entity.name}
          subtitle={`${entity.type} · ${entity.latitude.toFixed(3)}, ${entity.longitude.toFixed(3)}`}
          onClick={() => onSelect(entity.id)}
        />
      ))}
    </ResultGroup>
  )
}

function AddressResults({
  search,
  onSelect,
}: {
  search: AddressSearchState
  onSelect: (result: MapAddressResult) => void
}) {
  const { t } = useTranslation()
  if (search.status !== 'ready' || search.results.length === 0) return null
  return (
    <ResultGroup label={t('mapSearch.addresses')}>
      {search.results.map(result => (
        <ResultRow
          key={result.id}
          icon={<MapPinned className="h-3.5 w-3.5 text-primary" />}
          title={result.display}
          subtitle={formatAddressSubtitle(result)}
          onClick={() => onSelect(result)}
        />
      ))}
    </ResultGroup>
  )
}

function SearchFeedback({ model }: { model: SearchResultsModel }) {
  const { t } = useTranslation()
  const noLocalMatches = !model.coordinate && model.entityMatches.length === 0
  if (model.addressSearch.status === 'loading') {
    return (
      <Feedback
        icon={<Loader2 className="h-4 w-4 animate-spin" />}
        text={t('mapSearch.searching')}
      />
    )
  }
  if (model.addressSearch.status === 'error') {
    return <Feedback icon={<AlertCircle className="h-4 w-4" />} text={t('mapSearch.unavailable')} />
  }
  if (model.addressSearch.status === 'short' && noLocalMatches) {
    return <Feedback icon={<MapPin className="h-4 w-4" />} text={t('mapSearch.keepTyping')} />
  }
  if (
    model.addressSearch.status === 'ready' &&
    model.addressSearch.results.length === 0 &&
    noLocalMatches
  ) {
    return <Feedback icon={<MapPin className="h-4 w-4" />} text={t('mapSearch.noMatches')} />
  }
  return null
}

function Feedback({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <div className="px-3 py-4 text-center text-[11px] text-muted-foreground">
      <span className="mx-auto mb-1 flex h-4 w-4 items-center justify-center opacity-60">
        {icon}
      </span>
      {text}
    </div>
  )
}

function ResultGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="py-1">
      <div className="px-3 py-1 text-[9px] font-medium uppercase tracking-[0.18em] text-muted-foreground/70">
        {label}
      </div>
      {children}
    </div>
  )
}

function ResultRow({
  icon,
  title,
  subtitle,
  onClick,
}: {
  icon: ReactNode
  title: string
  subtitle?: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onMouseDown={event => event.preventDefault()}
      onClick={onClick}
      className="flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-accent/60"
    >
      <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[12px] font-medium text-foreground">{title}</p>
        {subtitle && (
          <p className="truncate font-mono text-[10px] tabular-nums text-muted-foreground">
            {subtitle}
          </p>
        )}
      </div>
    </button>
  )
}
